import React, { useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "../../App.css";
import { getApi, putApi } from "../../Services/apiService";
import { toast } from "react-toastify";
import {
  convertUTCToDate,
  convertUTCToTime,
  convertDateToTimestamp,
} from "../../Utils/dateUtils";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { enums } from "../../constants/enums";
import CustomDatePicker from "./Date/CustomDatePicker";
import DropdownSelect from "../FamilyTree/DropdownSelect";
import { states } from "../../constants/states";
import FamilyTreeNoMember from "./familyTreeNoMember";
import { msToDateTree } from "../../Utils/dateAndTime";
import { sortedGenderOptions } from "../../Utils/family-tree/dropdown";

const FamilyTreeListView = ({
  family_tree_id,
  idForTreeView,
  searchedKeyword,
  handleSearchChange,
  showSearchDropdown,
  setShowSearchDropdown,
  filteredUsers,
  handleUserSelect,
  searchContainerRef,
  searchDropdownStyle,
  userItemStyle,
  userImageStyle,
  onMemberUpdate, // Callback to refresh tree data after update
}) => {
  //Use the react-hook-form
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange", // Validate on change
    reValidateMode: "onChange", // Re-validate on change
  });

  const [isEditMode, setIsEditMode] = useState(false); // for the edit mode
  const [isSubmitting, setIsSubmitting] = useState(false); // track form submission state
  const [memberData, setMemberData] = useState({}); // store the api data
  const [viewMemberSiblings, setViewMemberSiblings] = useState([]); // store the sibling of fetched member
  const [viewMemberChildren, setViewMemberChildren] = useState([]); // store the children of fetched member
  const [otherMemberDetail, setOtherMemberDetail] = useState({}); // additional info
  const [originalFormData, setOriginalFormData] = useState(null); // store original form data for cancel
  const [originalMemberImageIds, setOriginalMemberImageIds] = useState([]); // track original member image IDs
  const [originalSpouseImageIds, setOriginalSpouseImageIds] = useState({}); // track original spouse image IDs by index

  //Gender options (M, F, others)
  const genderOptions = sortedGenderOptions.map(({ value, label }) => ({
    value: parseInt(value, 10),
    label,
  }));

  //State list
  const stateOptions = states.map((state) => ({ label: state, value: state }));

  //Living / Deceased option
  const livingStatusOptions = Object.entries(enums.present_or_deceased).map(
    ([key, label]) => ({
      value: parseInt(key),
      label: label,
    })
  );

  //Status of Member
  const livingStatus = watch("present_or_deceased");
  // Watch gender to disable middle name when Male
  const memberGender = watch("gender");

  //Martial status option - ordered as: Single/Divorced, In Relationship, Married, Separated, Remarried, Widowed
  const maritalStatusOptions = [
    { value: 1, label: enums.marital_status[1] }, // Single/Divorced
    { value: 3, label: enums.marital_status[3] }, // In Relationship
    { value: 2, label: enums.marital_status[2] }, // Married
    { value: 5, label: enums.marital_status[5] }, // Separated
    { value: 6, label: enums.marital_status[6] }, // Remarried
    { value: 4, label: enums.marital_status[4] }, // Widowed
  ];

  //Cell type options
  const cellTypeOptions = Object.entries(enums.cell_type).map(
    ([key, label]) => ({
      value: parseInt(key),
      label: label,
    })
  );

  // Watch this value to use it in your validation logic
  const generationLevel = watch("generation_level");

  // Also, watch the image field to show a preview if the user picks a new file
  const newImageFile = watch("image");

  //Track all spouse detail
  const { fields: spouseFields } = useFieldArray({
    control,
    name: "all_spouses",
  });

  const divorceOptions = [
    { label: "Yes", value: 1 },
    { label: "No", value: 2 },
  ];

  // Helper function to extract phone number without country code
  const extractPhoneNumber = (fullPhoneNumber, countryCode) => {
    if (!fullPhoneNumber) return null;

    // PhoneInput returns number like "1234567890" or "11234567890" (with country code)
    // We need to extract just the number part
    const phoneStr = String(fullPhoneNumber).trim();

    // If country code is provided, try to remove it
    if (countryCode) {
      const codeStr = String(countryCode).replace(/[^0-9]/g, ""); // Remove + and spaces
      if (phoneStr.startsWith(codeStr)) {
        return phoneStr.substring(codeStr.length);
      }
      // Also try with + prefix
      if (phoneStr.startsWith(countryCode)) {
        return phoneStr.substring(countryCode.length).trim();
      }
    }

    // Return as is if no country code match
    return phoneStr;
  };

  // Validate image file type (only jpg, jpeg, png)
  const validateImageType = (file) => {
    if (!file) return true; // No file is valid (optional)
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    const fileType = file.type || "";
    const fileName = file.name || "";
    const extension = fileName.split(".").pop()?.toLowerCase();

    return (
      validTypes.includes(fileType) ||
      ["jpg", "jpeg", "png"].includes(extension)
    );
  };

  // Helper function to check if form has any validation errors (including nested)
  const hasFormErrors = (errorsObj) => {
    if (!errorsObj || Object.keys(errorsObj).length === 0) {
      return false;
    }

    // Check all top-level errors
    for (const key in errorsObj) {
      if (errorsObj[key]) {
        // If it's a nested object (like all_spouses), recursively check
        if (typeof errorsObj[key] === "object" && errorsObj[key] !== null) {
          if (hasFormErrors(errorsObj[key])) {
            return true;
          }
        } else if (errorsObj[key].message) {
          // If it has a message, it's an error
          return true;
        }
      }
    }
    return false;
  };

  //Handle form submit
  const handleFormSubmit = async (data) => {
    // react-hook-form's handleSubmit already validates and prevents default
    // This function only runs if react-hook-form validation passes
    // But we'll add an extra safety check to ensure no errors

    // Double-check: If there are any validation errors, don't proceed
    if (hasFormErrors(errors)) {
      console.log("Form has validation errors:", errors);
      toast.error("Please fix all validation errors before submitting");
      return; // Don't call API if there are errors - edit mode stays on
    }

    // Set submitting state to disable Cancel button
    setIsSubmitting(true);

    try {
      // Validate image types
      const mainImage = data.image;
      if (mainImage && mainImage.length > 0 && typeof mainImage !== "string") {
        const imageFile = mainImage[0];
        if (!validateImageType(imageFile)) {
          toast.error("Only JPG, JPEG, and PNG image formats are allowed");
          setIsSubmitting(false);
          return;
        }
      }

      // Validate spouse images
      if (data.all_spouses && Array.isArray(data.all_spouses)) {
        for (let i = 0; i < data.all_spouses.length; i++) {
          const spouseImage = data.all_spouses[i]?.spouse_image_file;
          if (spouseImage && spouseImage.length > 0) {
            const imageFile = spouseImage[0];
            if (!validateImageType(imageFile)) {
              toast.error(
                `Spouse ${i + 1
                }: Only JPG, JPEG, and PNG image formats are allowed`
              );
              setIsSubmitting(false);
              return;
            }
          }
        }
      }

      // Extract phone number (without country code)
      const phoneNumber = extractPhoneNumber(
        data.phone_number,
        data.country_code
      );

      // Check if a new main image is uploaded - if yes, prepare to remove old images
      const hasNewMainImage = mainImage && mainImage.length > 0 && typeof mainImage !== "string";
      const removeMemberImageIds = hasNewMainImage ? originalMemberImageIds : [];

      // Prepare payload matching API response structure
      const payload = {
        // Main member fields
        first_name: data.first_name || "",
        middle_name: data.middle_name || "",
        last_name: data.last_name || "",
        suffix: data.suffix || "",
        maiden_name: data.maiden_name || "",
        nick_name: data.nick_name || "",
        gender: data.gender ? parseInt(data.gender) : null,
        dob: convertDateToTimestamp(data.dob),
        date_of_death:
          data.present_or_deceased === 2
            ? convertDateToTimestamp(data.date_of_death)
            : null,
        present_or_deceased: data.present_or_deceased
          ? parseInt(data.present_or_deceased)
          : null,
        relation:
          data.relation_to_top_of_line || data.relation_to_head_of_line || "",
        state_of_birth: data.state_of_birth || "",
        current_state_residing: data.current_state_residing || "",
        marital_status: data.marital_status
          ? parseInt(data.marital_status)
          : null,
        // wedding_anniversary: convertDateToTimestamp(data.wedding_anniversary),
        notes: data.notes || "",
        address: data.address || null,

        // Image removal: if new image is uploaded, remove old ones
        ...(removeMemberImageIds.length > 0 && {
          remove_image_ids: removeMemberImageIds,
        }),

        // User details
        email: data.email || "",
        country: data.country || null,
        bio_descendant_parent: data.bio_descendant_parent || "",
        bio_non_descendant_parent: data.bio_non_descendant_parent || "",
        step_parent: data.step_parent || "",
        adopted_parent: data.adopted_parent || "",
        phone_type: data.phone_type ? parseInt(data.phone_type) : null,
        phone: phoneNumber,
        country_code: data.country_code || "",

        // Spouses array
        spouses:
          data.all_spouses && Array.isArray(data.all_spouses)
            ? data.all_spouses.map((spouse, index) => {
              // Check if a new image is uploaded for this spouse
              const hasNewSpouseImage = 
                spouse.spouse_image_file && 
                spouse.spouse_image_file.length > 0 && 
                typeof spouse.spouse_image_file !== "string";
              
              // Get original image IDs for this spouse index
              const originalIdsForThisSpouse = originalSpouseImageIds[index] || [];
              
              // If new image is uploaded, prepare to remove old ones
              const removeSpouseImageIds = hasNewSpouseImage ? originalIdsForThisSpouse : [];

              // Build spouse payload according to Swagger spec
              const spousePayload = {
                // Include relation_history_id only if it exists and is not empty (for updates)
                // Omit it for new spouses (creates new relation_history entry)
                ...(spouse.relation_history_id && 
                    String(spouse.relation_history_id).trim() !== "" && {
                  relation_history_id: spouse.relation_history_id,
                }),
                spouse_first_name: spouse.spouse_first_name || "",
                spouse_middle_name: spouse.spouse_middle_name || null,
                spouse_last_name: spouse.spouse_last_name || "",
                spouse_suffix: spouse.spouse_suffix || null,
                spouse_maiden_name: spouse.spouse_maiden_name || null,
                spouse_nick_name: spouse.spouse_nick_name || null,
                spouse_gender: spouse.spouse_gender
                  ? parseInt(spouse.spouse_gender)
                  : null,
                spouse_dob: convertDateToTimestamp(spouse.spouse_dob),
                spouse_date_of_death:
                  spouse.spouse_present_or_deceased === 2
                    ? convertDateToTimestamp(spouse.spouse_date_of_death)
                    : null,
                spouse_present_or_deceased: spouse.spouse_present_or_deceased
                  ? parseInt(spouse.spouse_present_or_deceased)
                  : null,
                spouse_relation: spouse.spouse_relation || "",
                spouse_state_of_birth: spouse.spouse_state_of_birth || "",
                spouse_current_state_residing:
                  spouse.spouse_current_state_residing || null,
                spouse_email: spouse.spouse_email || "",
                spouse_phone: spouse.spouse_phone
                  ? extractPhoneNumber(
                    spouse.spouse_phone,
                    spouse.spouse_country_code
                  )
                  : null,
                spouse_country_code: spouse.spouse_country_code || "",
                spouse_phone_type: spouse.spouse_phone_type
                  ? parseInt(spouse.spouse_phone_type)
                  : null,
                spouse_wedding_anniversary: convertDateToTimestamp(
                  spouse.wedding_anniversary
                ),
                divorce: spouse.divorce ? parseInt(spouse.divorce) : null,
                divorce_date:
                  spouse.divorce === 1
                    ? convertDateToTimestamp(spouse.divorce_date)
                    : null,
                address: spouse.address || null,
                notes: spouse.notes || null,
                marital_status: spouse.marital_status
                  ? parseInt(spouse.marital_status)
                  : null,
                current_state_residing: spouse.current_state_residing || null,
                // remove_spouse_image_ids: JSON array (not stringified) inside the JSON string
                // If a new image is uploaded, remove old ones
                remove_spouse_image_ids: removeSpouseImageIds,
              };
              return spousePayload;
            })
            : [],
      };

      // Prepare FormData for multipart/form-data (for images)
      const formData = new FormData();

      // Add all payload fields except images
      // Handle empty strings - API accepts empty strings for some fields
      Object.keys(payload).forEach((key) => {
        if (key === "spouses") {
          // Append spouses as JSON array string (required by Swagger for multipart/form-data)
          // The entire spouses value must be a JSON array string
          formData.append("spouses", JSON.stringify(payload[key]));
        } else if (key === "remove_image_ids") {
          // Append remove_image_ids as JSON array string (required by Swagger for multipart/form-data)
          formData.append("remove_image_ids", JSON.stringify(payload[key]));
        } else if (payload[key] !== null && payload[key] !== undefined) {
          // Include empty strings (API expects them for some fields)
          formData.append(key, payload[key] === "" ? "" : payload[key]);
        }
      });

      // Handle main member image
      if (mainImage && mainImage.length > 0 && typeof mainImage !== "string") {
        formData.append("image", mainImage[0]);
      }

      // Handle spouse images
      if (data.all_spouses && Array.isArray(data.all_spouses)) {
        data.all_spouses.forEach((spouse, spouseIndex) => {
          const spouseImage = spouse.spouse_image_file;
          if (spouseImage && spouseImage.length > 0) {
            // Append with indexed field name
            formData.append(`spouse_image_${spouseIndex}`, spouseImage[0]);
          }
        });
      }

      // Console log the payload structure (without File objects for readability)
      console.log("=== PAYLOAD STRUCTURE (for API) ===");
      console.log("📋 Payload Object (matching API response format):");
      const payloadForLog = {
        ...payload,
        // Replace File objects with file info for logging
        spouses: payload.spouses.map((spouse, idx) => ({
          ...spouse,
          has_image_file:
            data.all_spouses?.[idx]?.spouse_image_file?.length > 0,
          image_file_name:
            data.all_spouses?.[idx]?.spouse_image_file?.[0]?.name || null,
        })),
      };
      console.log(JSON.stringify(payloadForLog, null, 2));

      console.log("\n📦 FormData entries (ready to send):");
      const formDataEntries = {};
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          formDataEntries[
            key
          ] = `File(${value.name}, ${value.size} bytes, ${value.type})`;
          console.log(
            `${key}: File(${value.name}, ${value.size} bytes, ${value.type})`
          );
        } else {
          formDataEntries[key] = value;
          // Truncate long values for readability
          const displayValue =
            typeof value === "string" && value.length > 100
              ? value.substring(0, 100) + "..."
              : value;
          console.log(`${key}: ${displayValue}`);
        }
      }
      console.log("\n📊 FormData Summary:", formDataEntries);

      console.log("=== END PAYLOAD ===", payload);

      // All validations passed, now send to API
      try {
        const response = await putApi(
          `/family-members-with-relations-v2/${idForTreeView}`,
          formData,
          // {
          //   headers: {
          //     "Content-Type": "multipart/form-data",
          //   },
          // }
        );

        if (response?.statusCode === 200) {
          toast.success("Member updated successfully!");
          setIsEditMode(false);

          // Refresh the tree view data after successful update
          if (onMemberUpdate && typeof onMemberUpdate === "function") {
            console.log("Triggering tree data refresh after member update...");
            try {
              await onMemberUpdate();
              console.log("Tree data refreshed successfully");
            } catch (refreshError) {
              console.error("Error refreshing tree data:", refreshError);
              // Don't show error toast here as the update was successful
              // User can manually refresh by switching views
            }
          }
        } else {
          toast.error(response?.message || "Failed to update member");
          // Keep edit mode on if update failed
        }
      } catch (apiError) {
        console.error("API Error:", apiError);
        toast.error(
          apiError?.message || "Failed to update member. Please try again."
        );
        // Keep edit mode on if API call failed
      } finally {
        // Reset submitting state after API call completes
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error preparing form data:", error);
      toast.error(error?.message || "Failed to prepare form data");
      // Keep edit mode on if there's an error
      setIsSubmitting(false);
    }
  };

  // Toggle Edit Mode
  const toggleEditMode = (e) => {
    e.preventDefault();
    setIsEditMode(!isEditMode);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    // Reset form to original values
    if (originalFormData) {
      reset(originalFormData);
    }
    setIsEditMode(false);
  };

  useEffect(() => {
    const fetchSingleMemberInfoForTreeView = async () => {
      if (!idForTreeView) return;
      try {
        const response = await getApi(
          `/family-tree-hierarchy-v2/${family_tree_id}?family_member_id=${idForTreeView}`
        );

        const data = await response?.data;
        if (response?.statusCode === 200) {
          console.log(
            "Check user_detail response",
            response?.data?.member?.user_details
          );
          const member = data?.member;
          const userDetails = member?.user_details || {}; // Use directly from API response

          setMemberData(member || {});
          setViewMemberSiblings(member?.siblings || []);
          setViewMemberChildren(member?.children || []);
          setOtherMemberDetail(userDetails); // Set state for other uses if needed

          // Extract and store original image IDs for removal when new images are uploaded
          // Member images: check member_images array (role 0 is for main member images)
          const memberImageIds = [];
          if (member?.member_images && Array.isArray(member.member_images)) {
            member.member_images.forEach((img) => {
              // Check for id field (could be id, image_id, or unique_image_id)
              const imageId = img.id || img.image_id || img.unique_image_id;
              if (imageId && (img.role === 0 || img.role === undefined)) {
                // role 0 is for main member images, or if role is undefined, include it
                memberImageIds.push(imageId);
              }
            });
          }
          setOriginalMemberImageIds(memberImageIds);

          // Spouse images: extract from all_spouses array
          const spouseImageIdsMap = {};
          if (member?.all_spouses && Array.isArray(member.all_spouses)) {
            member.all_spouses.forEach((spouse, index) => {
              const spouseImgIds = [];
              if (spouse?.spouse_images && Array.isArray(spouse.spouse_images)) {
                spouse.spouse_images.forEach((img) => {
                  // Check for id field (could be id, image_id, or unique_image_id)
                  const imageId = img.id || img.image_id || img.unique_image_id;
                  if (imageId) {
                    spouseImgIds.push(imageId);
                  }
                });
              }
              spouseImageIdsMap[index] = spouseImgIds;
            });
          }
          setOriginalSpouseImageIds(spouseImageIdsMap);

          // Debug logging
          console.log("Extracted image IDs:", {
            memberImageIds,
            spouseImageIdsMap,
          });

          // Generic helper function to validate enum values from API
          // API returns a number which should match enum keys
          const validateEnumValue = (apiValue, enumObject) => {
            if (apiValue === null || apiValue === undefined) {
              return undefined;
            }

            const parsedValue = parseInt(apiValue);

            // Validate: Check if the parsed number exists as a key in the enum object
            if (!isNaN(parsedValue) && enumObject[parsedValue] !== undefined) {
              return parsedValue;
            }

            return undefined;
          };

          // Helper function to validate state values from API
          // API returns a string which should exist in the states array
          const validateStateValue = (apiValue) => {
            if (
              apiValue === null ||
              apiValue === undefined ||
              apiValue === ""
            ) {
              return undefined;
            }

            const stateString = String(apiValue).trim();

            // Validate: Check if the state exists in the states array (case-insensitive)
            const foundState = states.find(
              (state) => state.toLowerCase() === stateString.toLowerCase()
            );

            if (foundState) {
              // Return the exact state name from the array (to match the option value)
              return foundState;
            }

            return undefined;
          };

          const formattedSpouses =
            member?.all_spouses?.map((spouse) => ({
              ...spouse,
              spouse_dob: spouse.spouse_dob
                ? msToDateTree(spouse.spouse_dob)
                : "",
              spouse_date_of_death: spouse.spouse_date_of_death
                ? msToDateTree(spouse.spouse_date_of_death)
                : "",
              wedding_anniversary: spouse.wedding_anniversary
                ? msToDateTree(spouse.wedding_anniversary)
                : "",
              // Validate spouse enum fields
              spouse_gender: validateEnumValue(
                spouse?.spouse_gender,
                enums.gender
              ),
              spouse_present_or_deceased: validateEnumValue(
                spouse?.spouse_present_or_deceased,
                enums.present_or_deceased
              ),
              divorce: validateEnumValue(spouse?.divorce, {
                1: "Yes",
                2: "No",
              }), // Divorce options
            })) || [];

          // Validate all enum-based fields from API
          const validatedGender = validateEnumValue(
            member?.gender ?? member?.user_details?.gender,
            enums.gender
          );

          const validatedMaritalStatus = validateEnumValue(
            member?.marital_status ?? member?.user_details?.marital_status,
            enums.marital_status
          );

          const validatedPresentOrDeceased = validateEnumValue(
            member?.present_or_deceased ??
            member?.user_details?.present_or_deceased,
            enums.present_or_deceased
          );

          // Check multiple possible field names for phone_type (API might use different names)
          const phoneTypeValue =
            member?.phone_type ??
            member?.primary_cell_type ??
            member?.user_details?.phone_type ??
            member?.user_details?.primary_cell_type;

          const validatedPhoneType = validateEnumValue(
            phoneTypeValue,
            enums.cell_type
          );

          // Validate state values from API - check multiple possible locations
          const stateOfBirthValue =
            member?.state_of_birth ?? member?.user_details?.state_of_birth;
          const validatedStateOfBirth = validateStateValue(stateOfBirthValue);

          const currentStateValue =
            member?.current_state_residing ??
            member?.user_details?.current_state_residing;
          const validatedCurrentState = validateStateValue(currentStateValue);

          // Debug logging (remove after testing)
          console.log("API Values:", {
            phoneTypeValue,
            validatedPhoneType,
            stateOfBirthValue,
            validatedStateOfBirth,
            currentStateValue,
            validatedCurrentState,
            member_phone_type: member?.phone_type,
            member_primary_cell_type: member?.primary_cell_type,
            user_details_phone_type: member?.user_details?.phone_type,
          });

          // Use userDetails directly (from API response) instead of otherMemberDetail state
          // because state updates are asynchronous and won't be available immediately
          let countryCode = userDetails?.country_code || "";
          const phoneNumber =
            userDetails?.phone_number || userDetails?.phone || "";

          // Clean country code: remove + sign and spaces if present
          countryCode = String(countryCode).replace(/^\+|\s/g, "");

          // Combine country code and phone number for PhoneInput
          // PhoneInput expects: country_code + phone_number (e.g., "178787878787877")
          const combinedPhone =
            countryCode && phoneNumber
              ? countryCode + phoneNumber
              : phoneNumber || "";

          console.log("Phone Number Debug:", {
            originalCountryCode: userDetails?.country_code,
            cleanedCountryCode: countryCode,
            phoneNumber,
            combinedPhone,
            userDetails,
          });

          // 2. FIX DATA MAPPING HERE
          const formData = {
            ...userDetails, // Use userDetails directly
            ...member,
            phone_number: combinedPhone,
            country_code: countryCode,
            all_spouses: formattedSpouses,
            dob: member?.dob ? msToDateTree(member.dob) : "",
            date_of_death: member?.date_of_death
              ? msToDateTree(member.date_of_death)
              : "",
            wedding_anniversary: member?.wedding_anniversary
              ? msToDateTree(member.wedding_anniversary)
              : "",

            // Map the API 'relation' field to the form inputs
            relation_to_top_of_line: member?.generation_level === 1 ? "Self" : (member?.relation || ""),
            relation_to_head_of_line: member?.relation || "",

            // Set validated enum values (numbers that match enum keys)
            // These values will be used by DropdownSelect to find and display the corresponding labels
            // Always use validated value - if undefined, it means the value doesn't match enum, so set to undefined to clear invalid values
            gender: validatedGender,
            marital_status: validatedMaritalStatus,
            present_or_deceased: validatedPresentOrDeceased,
            phone_type: validatedPhoneType,

            // Set validated state values (strings that exist in states array)
            // Always use validated value - if undefined, it means the value doesn't match states array, so set to undefined to clear invalid values
            state_of_birth: validatedStateOfBirth,
            current_state_residing: validatedCurrentState,
          };

          reset(formData);
          // Store original form data for cancel functionality
          setOriginalFormData(formData);
          // Don't show success toast on initial load, only on updates
        }
      } catch (error) {
        console.log(error);
        toast.error(error?.message || "Failed to fetch member detail");
      }
    };

    if (idForTreeView) {
      fetchSingleMemberInfoForTreeView();
    }
  }, [family_tree_id, idForTreeView, reset]); // added reset to dependencies

  return (
    <div className="list-view-start">
      <div className="search-box" ref={searchContainerRef}>
        <input
          type="text"
          placeholder="Search..."
          className="search-input w-auto"
          value={searchedKeyword}
          onChange={handleSearchChange}
          onFocus={() => searchedKeyword && setShowSearchDropdown(true)}
        />
        <i className="fas fa-search search-icon"></i>

        {showSearchDropdown && (
          <div style={searchDropdownStyle}>
            {filteredUsers.map((user, index) => (
              <div
                key={index}
                style={userItemStyle}
                onClick={() => handleUserSelect(user.id)}
              >
                <img
                  src={user.image || window.getPublicUrl("/assets/images/d-user.jpg")}
                  alt={user.name}
                  style={userImageStyle}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = window.getPublicUrl("/assets/images/d-user.jpg");
                  }}
                />
                <span>{user.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {!idForTreeView ? (
        <FamilyTreeNoMember />
      ) : (
        <div className="list-view">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <>
              <div className="col-md-6">
                <div className="header-div text-end">

                </div>
              </div>
            </>
            <div className="row">
              {/*className="col-md-8  col-md-12" */}
              <div
                className={
                  getValues("marital_status") === 2 || 
                    getValues("marital_status") === 6 ||
                    viewMemberSiblings.length > 0 ||
                    viewMemberChildren.length > 0
                    ? "col-md-8"
                    : "col-md-12"
                }
              >
                <div className="row box-shadow">
                  <div className="col-md-12 d-flex justify-content-between">
                    <h3 className="profile-view-hading w-50">
                      User Profile View
                    </h3>
                    {!isEditMode && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={toggleEditMode}
                      >
                        <i className="fas fa-edit me-1"></i> Edit Profile
                      </button>
                    )}
                    {/* --- SAVE / CANCEL BUTTONS --- */}
                    {isEditMode && (
                      <div className="d-flex">
                        <button
                          type="button"
                          className="button btn-outline me-2"
                          onClick={handleCancel}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-submit" disabled={isSubmitting}>
                          Save Changes
                        </button>
                      </div>
                    )}
                  </div>

                  {/*Profile Image */}
                  <div className="col-md-12 mt-3">
                    <div className="d-flex align-items-center">
                      <div
                        style={{
                          position: "relative",
                          cursor: isEditMode ? "pointer" : "default",
                        }}
                      >
                        <img
                          src={
                            newImageFile &&
                              typeof newImageFile !== "string" &&
                              newImageFile[0]
                              ? URL.createObjectURL(newImageFile[0])
                              : memberData?.image ||
                              window.getPublicUrl("/assets/images/d-user.jpg")
                          }
                          className="profile-img me-3"
                          alt="profile"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "50%",
                          }}
                          // Only open file dialog if isEditMode is true
                          onClick={() =>
                            isEditMode &&
                            document
                              .getElementById("profile_image_input")
                              .click()
                          }
                        />

                        {/* Only show camera icon in edit mode */}
                        {isEditMode && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "11px",
                              right: "15px",
                              background: "#fff",
                              borderRadius: "50%",
                              padding: "2px 5px",
                              border: "1px solid #ccc",
                              height: "35px",
                              width: "35px"
                            }}
                            onClick={() =>
                              document
                                .getElementById("profile_image_input")
                                .click()
                            }
                          >
                            <img src={window.getPublicUrl("/assets/images/Edit_fill.svg")} alt="" />
                          </div>
                        )}
                      </div>
                      <div>
                        <input
                          id="profile_image_input"
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          style={{ display: "none" }}
                          disabled={!isEditMode} // Disable input
                          {...register("image", {
                            // Validation logic...
                            validate: (value) => {
                              if (!isEditMode) return true; // Skip validation in view mode
                              if (parseInt(generationLevel) === 1) return true;

                              // Check if image is provided
                              if (
                                !value ||
                                (value.length === 0 && !memberData?.image)
                              ) {
                                return "Profile image is required.";
                              }

                              // Validate image type if file is selected
                              if (
                                value &&
                                value.length > 0 &&
                                typeof value !== "string"
                              ) {
                                const file = value[0];
                                if (!validateImageType(file)) {
                                  return "Only JPG, JPEG, and PNG image formats are allowed.";
                                }
                              }

                              return true;
                            },
                          })}
                        />
                        {errors.image && isEditMode && (
                          <div
                            className="text-danger"
                            style={{ fontSize: "12px", marginTop: "5px" }}
                          >
                            {errors.image.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/*Gender Field */}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">Gender</label>
                      <Controller
                        control={control}
                        name="gender"
                        rules={{
                          required: "Gender is required",
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <>
                            <DropdownSelect
                              options={genderOptions}
                              value={
                                value !== undefined && value !== null
                                  ? value
                                  : ""
                              }
                              onChange={(selectedValue) => {
                                // DropdownSelect passes the value directly (number from enum)
                                // This value matches the option.value (e.g., 1, 2, 3, 4)
                                onChange(selectedValue);
                              }}
                              placeholder="Select Gender"
                              maxMenuHeight={180}
                              disabled={!isEditMode}
                            />
                            {error && isEditMode && (
                              <span className="error">{error.message}</span>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/*First Name field */}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode}
                        {...register("first_name", {
                          required: "First Name is required",
                        })}
                      />
                      {errors.first_name && isEditMode && (
                        <span className="error">
                          {errors.first_name.message}
                        </span>
                      )}
                    </div>
                  </div>

                  {/*Middle Name field */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Middle Name</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode || memberGender === 1}
                        style={memberGender === 1 ? { backgroundColor: "#f5f5f5", opacity: 0.6, cursor: "not-allowed" } : {}}
                        {...register("middle_name")}
                      />
                    </div>
                  </div>

                  {/*Last Name Field */}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode}
                        {...register("last_name", {
                          required: "Last Name is required",
                        })}
                      />
                      {errors.last_name && isEditMode && (
                        <span className="error">
                          {errors.last_name.message}
                        </span>
                      )}
                    </div>
                  </div>

                  {/*Suffix field */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Suffix</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode || memberGender === 2}
                        style={memberGender === 2 ? { backgroundColor: "#f5f5f5", opacity: 0.6, cursor: "not-allowed" } : {}}
                        {...register("suffix")}
                      />
                    </div>
                  </div>

                  {/*Maiden Name field */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Maiden Name</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode || memberGender === 1}
                        style={memberGender === 1 ? { backgroundColor: "#f5f5f5", opacity: 0.6, cursor: "not-allowed" } : {}}
                        {...register("maiden_name")}
                      />
                    </div>
                  </div>

                  {/*Nick Name field */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Nick Name</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode}
                        {...register("nick_name")}
                      />
                    </div>
                  </div>

                  {/*Martial Status */}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">Marital Status</label>
                      <Controller
                        control={control}
                        name="marital_status"
                        rules={{
                          required: "Marital Status is required",
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <>
                            <DropdownSelect
                              options={maritalStatusOptions}
                              disabled={!isEditMode}
                              value={
                                value !== undefined && value !== null
                                  ? value
                                  : ""
                              }
                              onChange={(selectedValue) => {
                                onChange(selectedValue);
                              }}
                              placeholder="Select Marital Status"
                              maxMenuHeight={180}
                            />
                            {error && isEditMode && (
                              <span className="error">{error.message}</span>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/*Date of birth */}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">Date of Birth</label>
                      <Controller
                        control={control}
                        name="dob"
                        rules={{
                          required: "Date of Birth is required",
                          validate: (value) => {
                            const date = new Date(value);
                            if (isNaN(date.getTime())) {
                              return "Please enter a valid, complete date";
                            }
                            return true;
                          },
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <CustomDatePicker
                            value={value || ""}
                            onChange={onChange}
                            disabled={!isEditMode}
                            name="dob"
                            required={true}
                            error={!!error}
                            helperText={
                              error && isEditMode ? error.message : ""
                            }
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/*State of Birth */}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">U.S. State or Country of Birth</label>
                      <Controller
                        control={control}
                        name="state_of_birth"
                        rules={{
                          required: "State of Birth is required",
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <>
                            <DropdownSelect
                              options={stateOptions}
                              value={
                                value !== undefined &&
                                  value !== null &&
                                  value !== ""
                                  ? value
                                  : ""
                              }
                              onChange={(selectedValue) => {
                                onChange(selectedValue);
                              }}
                              placeholder="Select your state of birth"
                              disabled={!isEditMode}
                              maxMenuHeight={180}
                            />
                            {error && isEditMode && (
                              <span className="error">{error.message}</span>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/*Current State  */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Current U.S. State or Country Living</label>
                      <Controller
                        control={control}
                        name="current_state_residing"
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <DropdownSelect
                            options={stateOptions}
                            value={
                              value !== undefined &&
                                value !== null &&
                                value !== ""
                                ? value
                                : ""
                            }
                            onChange={(selectedValue) => {
                              onChange(selectedValue);
                            }}
                            placeholder={
                              isEditMode ? "Select your current state" : ""
                            }
                            disabled={!isEditMode}
                            maxMenuHeight={180}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Living / Deceased*/}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">
                        Living / Deceased
                      </label>
                      <Controller
                        control={control}
                        name="present_or_deceased"
                        rules={{
                          required: "Living / Deceased status is required",
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <>
                            <DropdownSelect
                              options={livingStatusOptions}
                              value={
                                value !== undefined && value !== null
                                  ? value
                                  : ""
                              }
                              onChange={(selectedValue) => {
                                onChange(selectedValue);
                              }}
                              placeholder="Select Status"
                              disabled={!isEditMode}
                              maxMenuHeight={180}
                            />
                            {error && isEditMode && (
                              <span className="error">{error.message}</span>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/* Conditionally Render Date of Death */}
                  {parseInt(livingStatus) === 2 && (
                    <div className="col-sm-4">
                      <div className="">
                        <label className="required-label">Date of Death</label>
                        <Controller
                          control={control}
                          name="date_of_death"
                          rules={{
                            required: "Date of Death is required",
                            validate: (value) => {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) {
                                return "Please enter a valid, complete date";
                              }
                              const dobValue = getValues("dob");

                              if (dobValue) {
                                const birthDate = new Date(dobValue);
                                // Check if Death Date is BEFORE Birth Date
                                if (date < birthDate) {
                                  return "Date of Death cannot be before Date of Birth";
                                }
                              }
                              return true;
                            },
                          }}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <CustomDatePicker
                              value={value || ""}
                              onChange={onChange}
                              name="date_of_death"
                              disabled={!isEditMode}
                              required={true}
                              error={!!error}
                              helperText={
                                error && isEditMode ? error.message : ""
                              }
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/*Email */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        {...register("email")}
                        readOnly={true}
                      />
                    </div>
                  </div>

                  {/*Cell Type */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Cell Type</label>
                      <Controller
                        control={control}
                        name="phone_type"
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <DropdownSelect
                            options={cellTypeOptions}
                            value={
                              value !== undefined && value !== null ? value : ""
                            }
                            onChange={(selectedValue) => {
                              onChange(selectedValue);
                            }}
                            placeholder={isEditMode ? "Select cell type" : ""}
                            disabled={!isEditMode}
                            maxMenuHeight={180}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/*Cell Number */}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">Cell Number</label>
                      <Controller
                        control={control}
                        name="phone_number"
                        rules={{
                          required: "Cell Number is required",
                          validate: (value) => {
                            // 1. Get the country data that we stored during onChange
                            const countryData = getValues(
                              "selected_country_data"
                            );

                            // If no value provided, required rule will handle it
                            if (!value || value.trim() === "") {
                              return true; // Let required rule handle empty values
                            }

                            // If user hasn't selected a country yet, do basic validation
                            if (!countryData || !countryData.format) {
                              if (value.length < 5) {
                                return "Invalid phone number (minimum 5 digits required)";
                              }
                              return true;
                            }

                            // 2. Count the dots '.' in the format string.
                            // Example India: "+.. .....-....." -> 12 dots (2 for code + 10 for number)
                            // The format includes the country code, so expectedLength includes both code and number
                            const expectedLength = (
                              countryData.format.match(/\./g) || []
                            ).length;

                            // Debug log (can be removed in production)
                            console.log("Phone Validation:", {
                              value: value,
                              valueLength: value.length,
                              expectedLength: expectedLength,
                              countryName: countryData.name,
                              dialCode: countryData.dialCode,
                              format: countryData.format,
                            });

                            // 3. Compare actual value length (which includes country code) with expected length
                            // PhoneInput value includes the country code, so we compare total length
                            if (value.length !== expectedLength) {
                              // Calculate phone length without code for the error message
                              const dialCodeLength = String(
                                countryData.dialCode || ""
                              ).length;
                              const phoneLengthOnly =
                                expectedLength - dialCodeLength;

                              return `Please enter a valid ${countryData.name} phone number.`;
                            }

                            return true;
                          },
                        }}
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <>
                            <PhoneInput
                              country={"us"} // Default country
                              value={value || ""}
                              disabled={!isEditMode}
                              onChange={(phoneValue, countryData) => {
                                // 1. Update the main phone_number field with the full string (for UI)
                                onChange(phoneValue);

                                // 2. Store the ENTIRE countryData object so validation can access the 'format'
                                // This is critical for country-specific validation
                                setValue("selected_country_data", countryData);

                                // 3. Update the hidden 'country_code' field using the data provided by the library
                                setValue("country_code", countryData.dialCode);
                              }}
                              inputStyle={{
                                width: "100%",
                                height: "38px",
                                borderColor: error ? "#dc3545" : undefined,
                              }}
                              containerStyle={{
                                marginBottom: error ? "5px" : "15px",
                              }}
                              inputProps={{
                                name: "phone_number",
                                required: true,
                              }}
                            />
                            {error && isEditMode && (
                              <span className="error">{error.message}</span>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  {/*Relation To Top Of The Line*/}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Relation to Top of the Line</label>
                      <input
                        type="text"
                        className="form-control"
                        {...register("relation_to_top_of_line")}
                        readOnly={true}
                        style={{ backgroundColor: "#e9ecef" }}
                      />
                    </div>
                  </div>
                  
                  {/*Bio Descendant Parent */}
                  <div className="col-sm-4">
                    <div className="">
                      <label className="required-label">
                        Bio Descendant Parent<span style={{textTransform: 'lowercase'}}>(s)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode}
                        placeholder="Include both parents if this is Start/Top of the Line only."
                        {...register("bio_descendant_parent", {
                          required: "Bio Descendant Parent(s) is required",
                        })}
                      />
                      {errors?.bio_descendant_parent && (
                        <span className="error">
                          {errors.bio_descendant_parent.message}
                        </span>
                      )}
                    </div>
                  </div>

                  {/*Bio Non-Descendant Parent */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Bio Non-Descendant Parent</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode}
                        {...register("bio_non_descendant_parent")}
                      />
                    </div>
                  </div>

                  {/*Step Parent */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Step Parent(s)</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode}
                        {...register("step_parent")}
                      />
                    </div>
                  </div>

                  {/*Adopted Parent(s) */}
                  <div className="col-sm-4">
                    <div className="">
                      <label>Adopted Parent(s)</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={!isEditMode}
                        {...register("adopted_parent")}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                {/*Ridht side chilren and sibling view */}
                {(viewMemberSiblings.length > 0 ||
                  viewMemberChildren.length > 0) && (
                    <div className="row box-shadow mt-0">
                      {/*Sibling of the view member */}
                      {viewMemberSiblings && viewMemberSiblings.length > 0 && (
                        <div className="col-md-6 child-sibling-data">
                          <h6 className="">Siblings</h6>
                          <ul>
                            {viewMemberSiblings.map((sibling) => (
                              <li key={sibling?.id}>
                                {sibling?.full_name || ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/*View Children */}
                      {viewMemberChildren && viewMemberChildren.length > 0 && (
                        <div className="col-md-6 child-sibling-data">
                          <h6 className="">Children</h6>
                          <ul>
                            {viewMemberChildren.map((children) => (
                              <li>{children?.full_name || ""}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                {/* DYNAMIC SPOUSE LIST */}
                {(getValues("marital_status") == "2" ||
                  getValues("marital_status") == "6") && (
                    <>
                      {spouseFields.map((spouse, index) => {
                        // 1. Watch values for conditional rendering specific to THIS spouse index
                        const currentSpouseStatus = watch(
                          `all_spouses.${index}.spouse_present_or_deceased`
                        );
                        const isDivorced = watch(`all_spouses.${index}.divorce`);
                        const spouseNewImage = watch(
                          `all_spouses.${index}.spouse_image_file`
                        ); // Watch for new file upload

                        return (
                          <div key={spouse.id} className="row box-shadow mt-0">
                            <div className="col-md-12">
                              <h3 className="profile-view-hading">
                                Spouse Details {index + 1}
                              </h3>
                            </div>

                            {/* 1. SPOUSE PROFILE IMAGE */}
                            <div className="col-md-12 mb-3">
                              <div className="d-flex align-items-center">
                                <div
                                  style={{
                                    position: "relative",
                                    cursor: isEditMode ? "pointer" : "default",
                                  }}
                                >
                                  <img
                                    src={
                                      // Logic: Show new blob if uploaded, otherwise show API URL, otherwise default
                                      spouseNewImage && spouseNewImage.length > 0
                                        ? URL.createObjectURL(spouseNewImage[0])
                                        : spouse.spouse_images &&
                                          spouse.spouse_images.length > 0
                                          ? spouse.spouse_images[0]?.image // Assuming API returns array of strings
                                          : window.getPublicUrl("/assets/images/d-user.jpg")
                                    }
                                    className="profile-img me-3"
                                    alt="spouse profile"
                                    style={{
                                      width: "100px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "50%",

                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src =
                                        window.getPublicUrl("/assets/images/d-user.jpg");
                                    }}
                                    // Click image to trigger input only in edit mode
                                    onClick={() =>
                                      isEditMode &&
                                      document
                                        .getElementById(
                                          `spouse_image_input_${index}`
                                        )
                                        .click()
                                    }
                                  />
                                  {/* Camera Icon Overlay */}
                                  {isEditMode && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: "11px",
                                        right: "15px",
                                        background: "#fff",
                                        borderRadius: "50%",
                                        padding: "2px 5px",
                                        border: "1px solid #ccc",
                                        height: "35px",
                                        width: "35px"
                                      }}
                                      onClick={() =>
                                        document
                                          .getElementById(
                                            `spouse_image_input_${index}`
                                          )
                                          .click()
                                      }
                                    >
                                      <img src={window.getPublicUrl("/assets/images/Edit_fill.svg")} alt="" />
                                    </div>
                                  )}
                                </div>

                                <div className="">
                                  <input
                                    id={`spouse_image_input_${index}`}
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png"
                                    style={{ display: "none" }}
                                    disabled={!isEditMode}
                                    {...register(
                                      `all_spouses.${index}.spouse_image_file`,
                                      {
                                        validate: (value) => {
                                          if (!isEditMode) return true;
                                          // If Generation 1, image is not required
                                          if (parseInt(generationLevel) === 1)
                                            return true;

                                          // Check if we have a new file OR an existing image from API
                                          const hasExistingImage =
                                            spouse.spouse_images &&
                                            spouse.spouse_images.length > 0;
                                          const hasNewFile =
                                            value && value.length > 0;

                                          if (!hasExistingImage && !hasNewFile) {
                                            return "Spouse image is required.";
                                          }

                                          // Validate image type if file is selected
                                          if (hasNewFile) {
                                            const file = value[0];
                                            if (!validateImageType(file)) {
                                              return "Only JPG, JPEG, and PNG image formats are allowed.";
                                            }
                                          }

                                          return true;
                                        },
                                      }
                                    )}
                                  />
                                  {errors?.all_spouses?.[index]
                                    ?.spouse_image_file &&
                                    isEditMode && (
                                      <div
                                        className="text-danger"
                                        style={{
                                          fontSize: "12px",
                                          marginTop: "5px",
                                        }}
                                      >
                                        {
                                          errors.all_spouses[index]
                                            .spouse_image_file.message
                                        }
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>

                            {/* 2. GENDER */}
                            <div className="col-sm-4">
                              <label className="required-label">Gender</label>
                              <Controller
                                control={control}
                                name={`all_spouses.${index}.spouse_gender`}
                                rules={{ required: "Gender is required" }}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <>
                                    <DropdownSelect
                                      options={genderOptions}
                                      value={
                                        value !== undefined && value !== null
                                          ? value
                                          : ""
                                      }
                                      onChange={(selectedValue) =>
                                        onChange(selectedValue)
                                      }
                                      placeholder="Select Gender"
                                      maxMenuHeight={180}
                                      disabled={!isEditMode}
                                    />
                                    {error && isEditMode && (
                                      <span className="error">
                                        {error.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>

                            {/* 3. FIRST NAME */}
                            <div className="col-sm-4">
                              <label className="required-label">First Name</label>
                              <input
                                type="text"
                                className="form-control"
                                disabled={!isEditMode}
                                {...register(
                                  `all_spouses.${index}.spouse_first_name`,
                                  {
                                    required: "First Name is required",
                                  }
                                )}
                              />
                              {errors?.all_spouses?.[index]?.spouse_first_name &&
                                isEditMode && (
                                  <span className="error">
                                    {
                                      errors.all_spouses[index].spouse_first_name
                                        .message
                                    }
                                  </span>
                                )}
                            </div>

                            {/* 4. LAST NAME */}
                            <div className="col-sm-4">
                              <label className="required-label">Last Name</label>
                              <input
                                type="text"
                                className="form-control"
                                disabled={!isEditMode}
                                {...register(
                                  `all_spouses.${index}.spouse_last_name`,
                                  {
                                    required: "Last Name is required",
                                  }
                                )}
                              />
                              {errors?.all_spouses?.[index]?.spouse_last_name &&
                                isEditMode && (
                                  <span className="error">
                                    {
                                      errors.all_spouses[index].spouse_last_name
                                        .message
                                    }
                                  </span>
                                )}
                            </div>

                            {/* 5. DATE OF BIRTH */}
                            <div className="col-sm-4">
                              <label className="required-label">
                                Date of Birth
                              </label>
                              <Controller
                                control={control}
                                name={`all_spouses.${index}.spouse_dob`}
                                rules={{ required: "Date of Birth is required" }}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <CustomDatePicker
                                    value={value || ""}
                                    onChange={onChange}
                                    name="spouse_dob"
                                    required={true}
                                    disabled={!isEditMode}
                                    error={!!error && isEditMode}
                                    helperText={
                                      error && isEditMode ? error.message : ""
                                    }
                                  />
                                )}
                              />
                            </div>

                            {/* 6. WEDDING ANNIVERSARY */}
                            <div className="col-sm-4">
                              <label className="required-label">
                                Wedding Anniversary
                              </label>
                              <Controller
                                control={control}
                                name={`all_spouses.${index}.wedding_anniversary`}
                                rules={{
                                  required: "Wedding Anniversary is required",
                                  validate: (value) => {
                                    // 1. Basic Date Check
                                    const weddingDate = new Date(value);
                                    if (isNaN(weddingDate.getTime()))
                                      return "Invalid date";

                                    // 2. DYNAMICALLY get THIS spouse's Date of Birth
                                    // We use the index to target the specific row
                                    const spouseDob = getValues(
                                      `all_spouses.${index}.spouse_dob`
                                    );

                                    if (spouseDob) {
                                      const birthDate = new Date(spouseDob);
                                      if (weddingDate < birthDate) {
                                        return "Wedding anniversary cannot be before Date of Birth";
                                      }
                                    }
                                    return true;
                                  },
                                }}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <CustomDatePicker
                                    value={value || ""}
                                    onChange={onChange}
                                    name="wedding_anniversary"
                                    required={true}
                                    disabled={!isEditMode}
                                    error={!!error && isEditMode}
                                    helperText={
                                      error && isEditMode ? error.message : ""
                                    }
                                  />
                                )}
                              />
                            </div>

                            {/* 7. LIVING / DECEASED */}
                            <div className="col-sm-4">
                              <label className="required-label">
                                Living / Deceased
                              </label>
                              <Controller
                                control={control}
                                name={`all_spouses.${index}.spouse_present_or_deceased`}
                                rules={{ required: "Status is required" }}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <>
                                    <DropdownSelect
                                      options={livingStatusOptions}
                                      value={
                                        value !== undefined && value !== null
                                          ? value
                                          : ""
                                      }
                                      onChange={(selectedValue) =>
                                        onChange(selectedValue)
                                      }
                                      placeholder="Select Status"
                                      maxMenuHeight={180}
                                      disabled={!isEditMode}
                                    />
                                    {error && isEditMode && (
                                      <span className="error">
                                        {error.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>

                            {/* 8. DATE OF DEATH (CONDITIONAL) */}
                            {/* 2 represents Deceased based on your enums */}
                            {parseInt(currentSpouseStatus) === 2 && (
                              <div className="col-sm-4">
                                <label className="required-label">
                                  Date of Death
                                </label>
                                <Controller
                                  control={control}
                                  name={`all_spouses.${index}.spouse_date_of_death`}
                                  rules={{
                                    required: "Date of Death is required",
                                    validate: (value) => {
                                      // A. Basic Check
                                      const deathDate = new Date(value);
                                      if (isNaN(deathDate.getTime()))
                                        return "Invalid date";

                                      // B. DYNAMIC VALIDATION: Get THIS spouse's DOB
                                      // We look up the specific row using [index]
                                      const spouseDob = getValues(
                                        `all_spouses.${index}.spouse_dob`
                                      );

                                      if (spouseDob) {
                                        const birthDate = new Date(spouseDob);
                                        if (deathDate < birthDate) {
                                          return "Date of Death cannot be before Date of Birth";
                                        }
                                      }
                                      return true;
                                    },
                                  }}
                                  render={({
                                    field: { onChange, value },
                                    fieldState: { error },
                                  }) => (
                                    <CustomDatePicker
                                      value={value || ""}
                                      onChange={onChange}
                                      name="spouse_date_of_death"
                                      required={true}
                                      disabled={!isEditMode}
                                      error={!!error && isEditMode}
                                      helperText={
                                        error && isEditMode ? error.message : ""
                                      }
                                    />
                                  )}
                                />
                              </div>
                            )}

                            {/* 9. DIVORCE */}
                            <div className="col-sm-4">
                              <label className="required-label">Divorced?</label>
                              <Controller
                                control={control}
                                name={`all_spouses.${index}.divorce`}
                                rules={{ required: "Divorce status is required" }}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <>
                                    <DropdownSelect
                                      options={divorceOptions}
                                      value={
                                        value !== undefined && value !== null
                                          ? value
                                          : ""
                                      }
                                      onChange={(selectedValue) =>
                                        onChange(selectedValue)
                                      }
                                      placeholder="Divorced?"
                                      maxMenuHeight={180}
                                      disabled={!isEditMode}
                                    />
                                    {error && isEditMode && (
                                      <span className="error">
                                        {error.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>

                            {/* 10. DIVORCE DATE (CONDITIONAL) */}
                            {/* 1 represents Yes based on your divorceOptions */}
                            {parseInt(isDivorced) === 1 && (
                              <div className="col-sm-4">
                                <label className="required-label">
                                  Divorce Date
                                </label>
                                <Controller
                                  control={control}
                                  name={`all_spouses.${index}.divorce_date`}
                                  rules={{
                                    required: "Divorce Date is required",
                                    validate: (value) => {
                                      // A. Basic Check 
                                      const divorceDate = new Date(value);
                                      if (isNaN(divorceDate.getTime()))
                                        return "Invalid date";

                                      // B. DYNAMIC VALIDATION: Get THIS spouse's Wedding Anniversary
                                      const weddingDateVal = getValues(
                                        `all_spouses.${index}.wedding_anniversary`
                                      );

                                      if (weddingDateVal) {
                                        const weddingDate = new Date(
                                          weddingDateVal
                                        );
                                        // Check if Divorce is BEFORE Wedding
                                        if (divorceDate < weddingDate) {
                                          return "Divorce Date cannot be before Wedding Anniversary";
                                        }
                                      }
                                      return true;
                                    },
                                  }}
                                  render={({
                                    field: { onChange, value },
                                    fieldState: { error },
                                  }) => (
                                    <CustomDatePicker
                                      value={value || ""}
                                      onChange={onChange}
                                      name="divorce_date"
                                      required={true}
                                      disabled={!isEditMode}
                                      error={!!error && isEditMode}
                                      helperText={
                                        error && isEditMode ? error.message : ""
                                      }
                                    />
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FamilyTreeListView;
